import React, { useState } from "react";
import {
  Users,
  Plus,
  Trash2,
  Check,
  SkipForward,
  AlertCircle,
} from "lucide-react";
import { clientsApi } from "../../../services/api";

interface ClientEntry {
  displayName: string;
  email: string;
  phone: string;
  type: "individual" | "organization";
}

interface AddClientsStepProps {
  onSkip: () => void;
  /**
   * The wizard calls this on Next so filled rows the user never saved are
   * not lost. It rejects if any row fails to save.
   */
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>;
}

const EMPTY_CLIENT: ClientEntry = {
  displayName: "",
  email: "",
  phone: "",
  type: "individual",
};

const AddClientsStep: React.FC<AddClientsStepProps> = ({ onSkip, saveRef }) => {
  const [clients, setClients] = useState<ClientEntry[]>([{ ...EMPTY_CLIENT }]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<Set<number>>(new Set());
  const [errors, setErrors] = useState<Record<number, string>>({});

  const updateClient = (index: number, field: keyof ClientEntry, value: string) => {
    setClients((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setErrors((prev) => ({ ...prev, [index]: "" }));
  };

  const addRow = () => {
    setClients((prev) => [...prev, { ...EMPTY_CLIENT }]);
  };

  const removeRow = (index: number) => {
    if (clients.length <= 1) return;
    setClients((prev) => prev.filter((_, i) => i !== index));
    // Shift saved indices above the removed row down by one
    setSaved((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
    setErrors({});
  };

  const saveClient = async (index: number): Promise<boolean> => {
    const client = clients[index];
    if (!client.displayName.trim()) {
      setErrors((prev) => ({ ...prev, [index]: "Name is required" }));
      return false;
    }

    setSaving(true);
    try {
      await clientsApi.create({
        displayName: client.displayName.trim(),
        type: client.type,
        emails: client.email ? [{ label: "main", email: client.email.trim() }] : [],
        phones: client.phone ? [{ label: "main", number: client.phone.trim() }] : [],
      });
      setSaved((prev) => new Set(prev).add(index));
      return true;
    } catch (err: any) {
      setErrors((prev) => ({
        ...prev,
        [index]: err.response?.data?.message || "Failed to create client",
      }));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAll = async () => {
    setSaving(true);
    let failed = 0;
    for (let i = 0; i < clients.length; i++) {
      if (saved.has(i) || !clients[i].displayName.trim()) continue;
      const ok = await saveClient(i);
      if (!ok) failed++;
    }
    setSaving(false);
    return failed;
  };

  // Expose an auto-save for the wizard's Next button. Reassigned every
  // render so it always sees the latest rows.
  if (saveRef) {
    saveRef.current = async () => {
      const failed = await saveAll();
      if (failed > 0) {
        throw new Error(
          `${failed} client${failed !== 1 ? "s" : ""} couldn't be saved. Fix the highlighted rows or remove them to continue.`
        );
      }
    };
  }

  const hasUnsaved = clients.some((c, i) => c.displayName.trim() && !saved.has(i));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="h-5 w-5" style={{ color: "#0F5132" }} />
            Add Your Clients
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Add a few clients to get started. You can always add more later.
          </p>
        </div>
        <button
          onClick={onSkip}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-slate-100 to-slate-50 text-slate-500 border border-slate-200 hover:from-slate-200 hover:to-slate-100 hover:text-slate-700 hover:border-slate-300 shadow-sm hover:shadow transition-all"
        >
          <SkipForward className="h-3.5 w-3.5" />
          Skip this step
        </button>
      </div>

      {/* Client rows */}
      <div className="space-y-3">
        {clients.map((client, i) => (
          <div
            key={i}
            className={`rounded-xl border p-4 transition-colors ${
              saved.has(i)
                ? "bg-emerald-50/60 border-emerald-300"
                : "bg-white border-slate-200"
            }`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start">
              {/* Name */}
              <div className="sm:col-span-4">
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={client.displayName}
                  onChange={(e) => updateClient(i, "displayName", e.target.value)}
                  placeholder="Client name"
                  disabled={saved.has(i)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 transition-colors disabled:bg-slate-50 disabled:text-slate-500"
                />
              </div>

              {/* Email */}
              <div className="sm:col-span-3">
                <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                <input
                  type="email"
                  value={client.email}
                  onChange={(e) => updateClient(i, "email", e.target.value)}
                  placeholder="email@example.com"
                  disabled={saved.has(i)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 transition-colors disabled:bg-slate-50 disabled:text-slate-500"
                />
              </div>

              {/* Phone */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">Phone</label>
                <input
                  type="tel"
                  value={client.phone}
                  onChange={(e) => updateClient(i, "phone", e.target.value)}
                  placeholder="+1..."
                  disabled={saved.has(i)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 transition-colors disabled:bg-slate-50 disabled:text-slate-500"
                />
              </div>

              {/* Type */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">Type</label>
                <select
                  value={client.type}
                  onChange={(e) => updateClient(i, "type", e.target.value)}
                  disabled={saved.has(i)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 transition-colors disabled:bg-slate-50 disabled:text-slate-500"
                >
                  <option value="individual">Individual</option>
                  <option value="organization">Organization</option>
                </select>
              </div>

              {/* Actions */}
              <div className="sm:col-span-1 flex items-end gap-1 pb-0.5">
                {saved.has(i) ? (
                  <div className="h-9 w-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Check className="h-4 w-4 text-emerald-600" />
                  </div>
                ) : (
                  <>
                    {clients.length > 1 && (
                      <button
                        onClick={() => removeRow(i)}
                        className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {errors[i] && (
              <div className="flex items-center gap-1.5 mt-2 text-xs text-red-600">
                <AlertCircle className="h-3 w-3" />
                {errors[i]}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add another */}
      <button
        onClick={addRow}
        className="flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-80"
        style={{ color: "#0F5132" }}
      >
        <Plus className="h-4 w-4" />
        Add another client
      </button>

      {/* Save all button */}
      {hasUnsaved && (
        <button
          onClick={saveAll}
          disabled={saving}
          className="w-full py-3 rounded-xl text-white font-bold text-sm transition-colors disabled:opacity-50"
          style={{ backgroundColor: "#0F5132" }}
        >
          {saving ? "Saving..." : `Save ${clients.filter((c, i) => c.displayName.trim() && !saved.has(i)).length} Client${clients.filter((c, i) => c.displayName.trim() && !saved.has(i)).length !== 1 ? "s" : ""}`}
        </button>
      )}

      {saved.size > 0 && (
        <p className="text-sm text-emerald-600 font-medium text-center">
          {saved.size} client{saved.size !== 1 ? "s" : ""} added successfully
        </p>
      )}
    </div>
  );
};

export default AddClientsStep;
